// src/pages/taxonomy/Brands/BrandsListPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BrandsAPI } from '../../../api/v3/taxonomy-api-v3';

const BASE = '/app/sales/bagspa/taxonomy';

const Badge = ({ active }) => (
  <span className={`badge ${active ? 'badge-success' : 'badge-secondary'}`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

export default function BrandsListPage() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per: 25, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters & page size
  const [qName, setQName] = useState('');
  const [per, setPer] = useState(25);

  const load = useCallback(async (page = 1, perOverride = per, q = qName) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (perOverride) params.set('per', String(perOverride));
      if (q) params.set('q', q);

      const data = await BrandsAPI.list(`?${params.toString()}`);
      const items = Array.isArray(data) ? data : (data?.items || data?.data || []);
      const m = data?.meta;

      setRows(items);
      setMeta(m && typeof m.total === 'number'
        ? { page: m.page || page, per: m.per || perOverride || 25, total: m.total }
        : { page: 1, per: items.length || perOverride || 25, total: items.length });
    } catch (e) {
      setError(e.message || 'Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  }, [per, qName]);

  useEffect(() => { load(1); }, [load]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this item?')) return;
    try {
      await BrandsAPI.destroy(id);
      await load(meta.page, per, qName);
    } catch (e) {
      alert(e.message);
    }
  }

  const totalPages = Math.max(1, Math.ceil(meta.total / (meta.per || per || 25)));
  const canPrev = meta.page > 1;
  const canNext = meta.page < totalPages;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 8, flexWrap: 'wrap' }}>
        <h3 className="mb-0">Brands</h3>
        <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            className="form-control"
            style={{ minWidth: 260 }}
            placeholder="Filter by name or code…"
            value={qName}
            onChange={(e) => setQName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(1, per, e.currentTarget.value); }}
          />
          <select
            className="form-select"
            style={{ width: 120 }}
            value={per}
            onChange={(e) => {
              const nextPer = parseInt(e.target.value, 10);
              setPer(nextPer);
              load(1, nextPer, qName);
            }}
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}/page</option>)}
          </select>
          <button className="btn btn-outline-secondary" onClick={() => load(1, per, qName)}>Apply</button>
          <Link className="btn btn-primary" to={`${BASE}/brands/new`}>New</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading ? (
        <div>Loading…</div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ID</th>
                  <th style={{ width: 120 }}>Code</th>
                  <th>Name</th>
                  <th style={{ width: 120 }}>Active</th>
                  <th style={{ width: 200 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? rows.map(row => (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td>{row.code || '—'}</td>
                    <td>{row.name}</td>
                    <td><Badge active={!!row.active} /></td>
                    <td>
                      <Link className="btn btn-link p-0 me-3" to={`${BASE}/brands/${row.id}/edit`}>Edit</Link>
                      <button className="btn btn-link text-danger p-0" onClick={() => handleDelete(row.id)}>Delete</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="text-muted">No data.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="text-muted small">
              Page {meta.page} of {totalPages} • Total {meta.total}
            </div>
            <div className="btn-group">
              <button className="btn btn-outline-secondary btn-sm" disabled={!canPrev} onClick={() => load(1, per, qName)}>&laquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={!canPrev} onClick={() => load(meta.page - 1, per, qName)}>&lsaquo; Prev</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={!canNext} onClick={() => load(meta.page + 1, per, qName)}>Next &rsaquo;</button>
              <button className="btn btn-outline-secondary btn-sm" disabled={!canNext} onClick={() => load(totalPages, per, qName)}>&raquo;</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
